/**
 * Shared formatting utilities for billing and subscriptions
 *
 * Centralizes common formatting logic to avoid duplication and ensure consistency
 * across the application.
 */

import type { BillingInterval } from '@loader/shared-types';

/**
 * Format price from cents to dollar string with billing interval suffix
 *
 * @param cents - Price in cents (e.g., 4900 = $49.00)
 * @param interval - Billing interval ('monthly' or 'yearly')
 * @param currency - Currency code ('USD', 'EUR', etc.)
 * @returns Formatted price string (e.g., "$49/mo", "€49/mo")
 *
 * @example
 * ```ts
 * formatPrice(4900, 'monthly', 'USD') // "$49/mo"
 * formatPrice(4900, 'monthly', 'EUR') // "€49/mo"
 * formatPrice(49900, 'yearly', 'USD') // "$499/year"
 * formatPrice(null, 'monthly', 'USD') // "Custom"
 * ```
 */
export function formatPrice(
  cents: number | null,
  interval: BillingInterval = 'monthly',
  currency: string = 'EUR'
): string {
  if (cents === null) return 'Custom';
  if (cents === 0) return 'Free';

  const amount = (cents / 100).toFixed(0);
  const suffix = interval === 'yearly' ? '/year' : '/mo';

  // Map currency codes to symbols
  const currencySymbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
  };

  const symbol = currencySymbols[currency] || currency;
  return `${symbol}${amount}${suffix}`;
}

/**
 * Format storage limit from MB to GB string
 *
 * @param mb - Storage limit in megabytes
 * @returns Formatted storage string (e.g., "5 GB", "Unlimited")
 *
 * @example
 * ```ts
 * formatStorageLimit(5120) // "5 GB"
 * formatStorageLimit(10240) // "10 GB"
 * formatStorageLimit(null) // "Unlimited"
 * ```
 */
export function formatStorageLimit(mb: number | null): string {
  if (mb === null) return 'Unlimited';
  const gb = mb / 1024;
  return `${gb.toFixed(0)} GB`;
}

// Re-export from shared-types for backwards compatibility
export { formatBytes, formatFileSize } from '@loader/shared-types';

/**
 * Format number with commas for thousands separator
 *
 * @param num - Number to format
 * @returns Formatted number string (e.g., "1,000", "50,000")
 *
 * @example
 * ```ts
 * formatNumber(1000) // "1,000"
 * formatNumber(50000) // "50,000"
 * formatNumber(null) // "Unlimited"
 * ```
 */
export function formatNumber(num: number | null): string {
  if (num === null) return 'Unlimited';
  return num.toLocaleString();
}

// ============================================================================
// Date Formatting Utilities
// ============================================================================
// All date formatters use the browser's locale by default (undefined locale)
// This ensures dates are displayed according to the user's browser settings

/**
 * Format a date string to a short date format using browser locale
 *
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string (e.g., "15/01/2024" or "01/15/2024" depending on locale)
 *
 * @example
 * ```ts
 * formatDate("2024-01-15T10:30:00Z") // "15/01/2024" (UK) or "01/15/2024" (US)
 * formatDate(new Date()) // Today's date in locale format
 * ```
 */
export function formatDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return '-';

  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return '-';

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Format a date string to include time using browser locale
 *
 * @param dateString - ISO date string or Date object
 * @returns Formatted date+time string (e.g., "15/01/2024, 10:30" depending on locale)
 *
 * @example
 * ```ts
 * formatDateTime("2024-01-15T10:30:00Z") // "15/01/2024, 10:30" (UK)
 * ```
 */
export function formatDateTime(dateString: string | Date | null | undefined): string {
  if (!dateString) return '-';

  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return '-';

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a date string to a friendly short format using browser locale
 *
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string (e.g., "Jan 15, 2024")
 *
 * @example
 * ```ts
 * formatDateShort("2024-01-15T10:30:00Z") // "Jan 15, 2024"
 * ```
 */
export function formatDateShort(dateString: string | Date | null | undefined): string {
  if (!dateString) return '-';

  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return '-';

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date string to a friendly format with time using browser locale
 *
 * @param dateString - ISO date string or Date object
 * @returns Formatted date+time string (e.g., "Jan 15, 2024, 10:30 AM")
 *
 * @example
 * ```ts
 * formatDateTimeShort("2024-01-15T10:30:00Z") // "Jan 15, 2024, 10:30 AM"
 * ```
 */
export function formatDateTimeShort(dateString: string | Date | null | undefined): string {
  if (!dateString) return '-';

  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return '-';

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a version date with time for version lists
 *
 * @param dateString - ISO date string
 * @returns Formatted version date (e.g., "Jan 15, 2024, 10:30")
 */
export function formatVersionDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a duration in milliseconds to a human-readable string
 *
 * @param ms - Duration in milliseconds
 * @returns Formatted duration string (e.g., "1.5s", "2m 30s", "1h 5m")
 *
 * @example
 * ```ts
 * formatDuration(1500) // "1.5s"
 * formatDuration(65000) // "1m 5s"
 * formatDuration(3665000) // "1h 1m"
 * formatDuration(null) // "-"
 * ```
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '-';

  // Less than a second
  if (ms < 1000) {
    return `${ms}ms`;
  }

  // Less than a minute
  if (ms < 60000) {
    const seconds = ms / 1000;
    return `${seconds.toFixed(1)}s`;
  }

  // Less than an hour
  if (ms < 3600000) {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }

  // Hours and minutes
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

/**
 * Compute a human-readable duration between two ISO timestamps.
 * Returns '-' when start/end are missing or duration is sub-millisecond.
 */
export function getOperationDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt || !completedAt) return '-';
  const durationMs = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (durationMs < 1) return '-';
  return formatDuration(durationMs);
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "yesterday")
 *
 * Uses the browser's Intl.RelativeTimeFormat for localized output.
 *
 * @example
 * ```ts
 * formatRelativeTime(new Date(Date.now() - 5000)) // "5 seconds ago"
 * formatRelativeTime("2024-01-15T10:00:00Z") // "2 days ago"
 * formatRelativeTime(null) // "-"
 * ```
 */
export function formatRelativeTime(dateString: string | Date | null | undefined): string {
  if (!dateString) return '-';

  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return '-';

  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Use relative time format
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  if (diffSeconds < 60) {
    return rtf.format(-diffSeconds, 'second');
  }
  if (diffMinutes < 60) {
    return rtf.format(-diffMinutes, 'minute');
  }
  if (diffHours < 24) {
    return rtf.format(-diffHours, 'hour');
  }
  if (diffDays < 7) {
    return rtf.format(-diffDays, 'day');
  }

  // Fall back to short date for older dates
  return formatDateShort(date);
}
